import { ref } from 'vue';
import type { FontRecord } from '@codot/contracts';
import { fontsApi } from '../api/fonts.api';

/** The organization's custom fonts, loaded once and shared (pickers, previews, the Fonts tab). */
const fonts = ref<FontRecord[] | null>(null);
let loading: Promise<void> | null = null;
const registered = new Set<string>();

export function refreshFonts(): Promise<void> {
  loading = fontsApi
    .list()
    .then((list) => void (fonts.value = list))
    .catch(() => void (fonts.value ??= []))
    .finally(() => (loading = null));
  return loading;
}

export function useFonts() {
  if (!fonts.value && !loading) void refreshFonts();
  return { fonts, refresh: refreshFonts };
}

/** Browser-side name of a custom font (each is registered once with the FontFace API, like the player loads it). */
const cssName = (id: string) => `codot-font-${id}`;

/** CSS `font-family` for an app's font, or undefined (the default look). */
export function fontFamilyCss(fontId: string | null | undefined): string | undefined {
  if (!fontId) return undefined;
  const f = useFonts().fonts.value?.find((x) => x.id === fontId);
  if (!f) return undefined;
  if (!registered.has(f.id) && typeof FontFace !== 'undefined') {
    registered.add(f.id);
    const face = new FontFace(cssName(f.id), `url(${JSON.stringify(f.fileUrl)})`);
    void face.load().then((loaded) => document.fonts.add(loaded), () => registered.delete(f.id));
  }
  return `"${cssName(f.id)}", system-ui, sans-serif`;
}
